import React, { createContext, useContext, useEffect, useLayoutEffect, useState, ReactNode } from 'react';
import { themes, DEFAULT_THEME, Theme } from '@/shared/constants/themes';
import { useCustomer } from '@/app/providers';
import { CustomerRepository } from '@/services/firebase/customer-repository';

interface ThemeContextType {
  theme: Theme;
  themeName: string;
  setTheme: (themeName: string) => void;
  availableThemes: Theme[];
  organizationId?: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  organizationId?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, organizationId }) => {
  // Utiliser useCustomer de manière défensive
  let customer: any = null;
  let customerLoading = false;
  let refreshCustomer: (() => Promise<void>) | null = null;

  try {
    const customerContext = useCustomer();
    customer = customerContext.customer;
    customerLoading = customerContext.isLoading;
    refreshCustomer = customerContext.refreshCustomer;
  } catch (error) {
    // CustomerProvider n'est pas disponible, continuer sans customer
    console.warn('⚠️ CustomerProvider non disponible, utilisation du thème par défaut');
  }

  // Utiliser le thème du customer ou "default" (violet) par défaut
  const getThemeFromCustomer = (): string => {
    if (customer?.theme && themes[customer.theme]) {
      return customer.theme;
    }
    return DEFAULT_THEME; // "default" = thème violet
  };

  const [themeName, setThemeNameState] = useState<string>(() => {
    // Au premier rendu, utiliser le thème du customer si disponible, sinon "default"
    return customer?.theme && themes[customer.theme] ? customer.theme : DEFAULT_THEME;
  });
  const theme = themes[themeName] || themes[DEFAULT_THEME];

  // Recharger le thème quand le customer change
  useEffect(() => {
    if (!customerLoading && customer) {
      const customerTheme = getThemeFromCustomer();
      setThemeNameState(customerTheme);
    } else if (!customerLoading && !customer) {
      // Si pas de customer (non connecté), utiliser le thème par défaut
      setThemeNameState(DEFAULT_THEME);
    }
  }, [customer, customerLoading]);

  // Appliquer le thème immédiatement au chargement et lors des changements
  // Utiliser useLayoutEffect pour appliquer avant le premier rendu (évite le flash)
  useLayoutEffect(() => {
    const root = document.documentElement;

    // Supprimer toutes les classes de thème existantes
    Object.keys(themes).forEach((name) => {
      root.classList.remove(`theme-${name}`);
    });

    // Ajouter la classe du thème actif
    root.classList.add(`theme-${themeName}`);

    // Mapper les couleurs du thème vers les variables CSS utilisées dans l'application
    const themeColors = theme.colors;

    // Variables primaires (utilisées directement par Tailwind)
    // Mettre à jour toutes les variables CSS
    root.style.setProperty('--primary', themeColors.primary);
    root.style.setProperty('--primary-light', themeColors.primaryLight);
    root.style.setProperty('--primary-dark', themeColors.primaryDark);
    root.style.setProperty('--secondary', themeColors.secondary);
    root.style.setProperty('--secondary-light', themeColors.secondaryLight);
    root.style.setProperty('--secondary-dark', themeColors.secondaryDark);
    root.style.setProperty('--accent', themeColors.accent || themeColors.primary);
    root.style.setProperty('--accent-light', themeColors.accentLight || themeColors.primaryLight);
    root.style.setProperty('--accent-dark', themeColors.accentDark || themeColors.primaryDark);
    root.style.setProperty('--success', themeColors.success);
    root.style.setProperty('--success-light', themeColors.successLight);
    root.style.setProperty('--success-dark', themeColors.successDark);
    root.style.setProperty('--warning', themeColors.warning);
    root.style.setProperty('--warning-light', themeColors.warningLight);
    root.style.setProperty('--warning-dark', themeColors.warningDark);
    root.style.setProperty('--error', themeColors.error);
    root.style.setProperty('--error-light', themeColors.errorLight);
    root.style.setProperty('--error-dark', themeColors.errorDark);
    root.style.setProperty('--background', themeColors.background);
    root.style.setProperty('--background-secondary', themeColors.backgroundSecondary);
    root.style.setProperty('--background-tertiary', themeColors.backgroundTertiary);
    root.style.setProperty('--foreground', themeColors.foreground);
    root.style.setProperty('--foreground-secondary', themeColors.foregroundSecondary);
    root.style.setProperty('--surface', themeColors.surface);
    root.style.setProperty('--surface-elevated', themeColors.surfaceElevated);
    root.style.setProperty('--surface-outlined', themeColors.surfaceOutlined);
    root.style.setProperty('--border', themeColors.border);
    root.style.setProperty('--border-light', themeColors.borderLight);
    root.style.setProperty('--border-medium', themeColors.borderMedium);
    root.style.setProperty('--border-dark', themeColors.borderDark);
    root.style.setProperty('--link-active', themeColors.linkActive);

    // Texte/icône sur fond coloré (contraste garanti sur primary, success, warning)
    root.style.setProperty('--on-primary', '#ffffff');
    root.style.setProperty('--on-success', '#ffffff');
    root.style.setProperty('--on-warning', '#ffffff');

    root.style.setProperty('--gradient-primary', `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`);
    root.style.setProperty('--gradient-primary-hover', `linear-gradient(to right, ${themeColors.primaryDark}, ${themeColors.secondaryDark})`);
    root.style.setProperty('--gradient-primary-light', `linear-gradient(to right, ${themeColors.primaryLight}, ${themeColors.secondaryLight})`);

    // Variables pour les boutons : dégradé uniquement pour les thèmes "gradient" et "pastel", sinon couleur unie
    const useGradient = themeName === 'gradient' || themeName === 'pastel';
    root.style.setProperty('--button-primary', useGradient
      ? `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`
      : themeColors.primary);
    root.style.setProperty('--button-primary-hover', useGradient
      ? `linear-gradient(to right, ${themeColors.primaryDark}, ${themeColors.secondaryDark})`
      : themeColors.primaryDark);
    root.style.setProperty('--button-secondary', useGradient
      ? `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`
      : themeColors.secondary);
    root.style.setProperty('--button-secondary-hover', useGradient
      ? `linear-gradient(to right, ${themeColors.primaryDark}, ${themeColors.secondaryDark})`
      : themeColors.secondaryDark);

    // Debug: Vérifier que les variables sont bien appliquées
    if (process.env.NODE_ENV === 'development') {
      const computedPrimary = getComputedStyle(root).getPropertyValue('--primary').trim();
      const computedBackground = getComputedStyle(root).getPropertyValue('--background').trim();
      const computedSurface = getComputedStyle(root).getPropertyValue('--surface').trim();
      const computedLinkActive = getComputedStyle(root).getPropertyValue('--link-active').trim();
      console.log('🎨 Theme applied:', {
        organizationId,
        themeName,
        primary: { expected: themeColors.primary, computed: computedPrimary, match: computedPrimary === themeColors.primary },
        background: { expected: themeColors.background, computed: computedBackground, match: computedBackground === themeColors.background },
        surface: { expected: themeColors.surface, computed: computedSurface, match: computedSurface === themeColors.surface },
        linkActive: { expected: themeColors.linkActive, computed: computedLinkActive, match: computedLinkActive === themeColors.linkActive },
      });
    }
  }, [themeName, theme, organizationId]);

  const setTheme = async (newThemeName: string) => {
    if (themes[newThemeName]) {
      setThemeNameState(newThemeName);

      // Sauvegarder dans Firestore si customer est connecté
      if (customer?.id && refreshCustomer) {
        try {
          await CustomerRepository.updateTheme(customer.id, newThemeName);
          console.log(`✅ Thème sauvegardé dans Firestore: ${newThemeName}`);
          // Rafraîchir le customer pour synchroniser le contexte
          await refreshCustomer();
        } catch (error) {
          console.error('❌ Erreur lors de la sauvegarde du thème:', error);
          // Ne pas bloquer le changement de thème en cas d'erreur
        }
      }
    }
  };

  const availableThemes = Object.values(themes);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeName,
        setTheme,
        availableThemes,
        organizationId,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

